/**
 * Slide Cart — AJAX-driven cart drawer
 *
 * Depends on jQuery (already loaded by the theme) and
 * window.SlidecartConfig (set in snippets/slidecart.liquid).
 */
(function ($) {
  "use strict";

  var renderVersion = 0;
  var productCache = {};
  var externalCartRefreshTimer = null;
  var cartReconcileTimer = null;
  var cartMutationQueue = [];
  var cartMutationRunning = false;
  var cartMutationNeedsReconcile = false;
  var cartRenderPending = false;
  var latestCart = null;
  var lastFocusedElement = null;
  var cfg = window.SlidecartConfig || {};
  var moneyFormat = cfg.moneyFormat || "${{amount}}";
  var shopifyRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";

  /* ---------------------------------------------------------------
     Helpers
  --------------------------------------------------------------- */

  function formatMoney(cents) {
    if (typeof Shopify !== "undefined" && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, moneyFormat);
    }
    return "$" + (cents / 100).toFixed(2);
  }

  function htmlEncode(str) {
    var el = document.createElement("div");
    el.appendChild(document.createTextNode(str));
    return el.innerHTML;
  }

  function imgResize(url, size) {
    if (!url) return "";
    return url.replace(/(\.[^.]*)$/, "_" + size + "$1").replace("http:", "");
  }

  function isCartMutationUrl(url) {
    return /\/cart\/(add|change|update|clear)(\.js)?(?:[?#]|$)/.test(url || "");
  }

  function scheduleExternalCartRefresh() {
    latestCart = null;
    clearTimeout(externalCartRefreshTimer);
    externalCartRefreshTimer = setTimeout(function () {
      if (!$("#slidecart-wrapper").hasClass("active")) return;
      if (typeof window.slidecartRender === "function") {
        window.slidecartRender();
      }
    }, 120);
  }

  function markSlidecartOwnedRequest(xhr) {
    if (xhr) xhr.__slidecartOwnedRequest = true;
  }

  function ownedAjax(options) {
    var originalBeforeSend = options.beforeSend;

    options.beforeSend = function (xhr) {
      markSlidecartOwnedRequest(xhr);
      if (originalBeforeSend) originalBeforeSend(xhr);
    };

    return $.ajax(options);
  }

  function isCartState(value) {
    return value && typeof value.item_count === "number" && $.isArray(value.items);
  }

  function getCartItemIdentity(item) {
    if (!item) return null;

    var properties = item.properties || {};
    var propertyKeys = Object.keys(properties).sort();
    var normalizedProperties = {};

    for (var i = 0; i < propertyKeys.length; i++) {
      normalizedProperties[propertyKeys[i]] = properties[propertyKeys[i]];
    }

    return JSON.stringify({
      variantId: item.id,
      properties: normalizedProperties,
      sellingPlanId: item.selling_plan_allocation
        ? item.selling_plan_allocation.selling_plan.id
        : null,
    });
  }

  function findCartItem(cart, key, identity) {
    if (!cart || !cart.items) return null;

    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].key === key) return cart.items[i];
    }

    if (!identity) return null;

    for (var j = 0; j < cart.items.length; j++) {
      if (getCartItemIdentity(cart.items[j]) === identity) return cart.items[j];
    }

    return null;
  }

  function findSlidecartItem(key, identity) {
    var $item = $('[data-slidecart-item][data-key="' + key + '"]');
    if ($item.length || !identity) return $item;

    $("[data-slidecart-item]").each(function () {
      var $candidate = $(this);
      if ($candidate.data("slidecartIdentity") === identity) {
        $item = $candidate;
        return false;
      }
    });

    return $item;
  }

  function resolveCurrentCartItem(key, identity) {
    var deferred = $.Deferred();

    function resolve(cart) {
      var item = findCartItem(cart, key, identity);
      if (item) {
        deferred.resolve(item, cart);
      } else {
        deferred.reject();
      }
    }

    if (latestCart) {
      resolve(latestCart);
    } else {
      $.getJSON(shopifyRoot + "cart.js")
        .done(function (cart) {
          latestCart = cart;
          resolve(cart);
        })
        .fail(function () {
          deferred.reject.apply(deferred, arguments);
        });
    }

    return deferred.promise();
  }

  function scheduleCartReconcile() {
    if (!cartMutationNeedsReconcile && !cartRenderPending) return;

    clearTimeout(cartReconcileTimer);
    cartReconcileTimer = setTimeout(function () {
      if (cartMutationRunning || cartMutationQueue.length) {
        scheduleCartReconcile();
        return;
      }

      cartMutationNeedsReconcile = false;
      cartRenderPending = false;
      renderCart();
    }, 100);
  }

  function processCartMutationQueue() {
    if (cartMutationRunning) return;

    var mutation = cartMutationQueue.shift();
    if (!mutation) {
      scheduleCartReconcile();
      return;
    }

    cartMutationRunning = true;
    var request;

    try {
      request = mutation.execute();
    } catch (error) {
      request = $.Deferred().reject(error).promise();
    }

    $.when(request).done(function (result) {
      try {
        if (isCartState(result)) {
          latestCart = result;
        } else if (mutation.changesCart !== false) {
          // Force key resolution before the next line mutation.
          latestCart = null;
        }
        if (mutation.onSuccess) mutation.onSuccess(result);
      } finally {
        cartMutationRunning = false;
        processCartMutationQueue();
      }
    }).fail(function () {
      try {
        if (mutation.changesCart !== false) {
          latestCart = null;
        }
        if (mutation.onError) mutation.onError.apply(null, arguments);
      } finally {
        cartMutationRunning = false;
        processCartMutationQueue();
      }
    });
  }

  function enqueueCartMutation(mutation) {
    clearTimeout(cartReconcileTimer);

    if (mutation.reconcile !== false) {
      cartMutationNeedsReconcile = true;
    }

    // Invalidate cart GETs only when this write changes visible cart state.
    if (mutation.invalidateRender !== false) {
      renderVersion++;
    }
    cartMutationQueue.push(mutation);
    processCartMutationQueue();
  }

  function installExternalCartRefreshBridge() {
    if (window.__slidecartExternalCartBridgeInstalled) return;
    window.__slidecartExternalCartBridgeInstalled = true;

    if (window.fetch && !window.fetch.__slidecartRefreshPatched) {
      var originalFetch = window.fetch;
      var wrappedFetch = function (input, init) {
        var requestUrl = typeof input === "string" ? input : (input && input.url) || "";
        var requestMethod = (
          (init && init.method) ||
          (typeof input !== "string" && input && input.method) ||
          "GET"
        ).toUpperCase();

        return originalFetch.apply(this, arguments).then(function (response) {
          if (response && response.ok && requestMethod !== "GET" && isCartMutationUrl(requestUrl)) {
            scheduleExternalCartRefresh();
          }
          return response;
        });
      };

      wrappedFetch.__slidecartRefreshPatched = true;
      window.fetch = wrappedFetch;
    }

    var xhrProto = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
    if (xhrProto && !xhrProto.__slidecartRefreshPatched) {
      var originalOpen = xhrProto.open;
      var originalSend = xhrProto.send;

      xhrProto.open = function (method, url) {
        this.__slidecartRequestMethod = (method || "GET").toUpperCase();
        this.__slidecartWatchCartMutation =
          this.__slidecartRequestMethod !== "GET" && isCartMutationUrl(url);

        return originalOpen.apply(this, arguments);
      };

      xhrProto.send = function () {
        if (this.__slidecartWatchCartMutation && !this.__slidecartRefreshListenerAttached) {
          this.__slidecartRefreshListenerAttached = true;
          this.addEventListener("loadend", function () {
            if (!this.__slidecartOwnedRequest && this.status >= 200 && this.status < 300) {
              scheduleExternalCartRefresh();
            }
          });
        }

        return originalSend.apply(this, arguments);
      };

      xhrProto.__slidecartRefreshPatched = true;
    }
  }

  /* ---------------------------------------------------------------
     Progress bar
  --------------------------------------------------------------- */

  function updateProgressBar(cartTotalCents) {
    if (cfg.useIntelligems || !cfg.progressEnabled) return;

    var $msg = $("[data-slidecart-progress-message]");
    var $fill = $("[data-slidecart-progress-fill]");
    if (!$msg.length) return;

    var thresholdDollars = (cfg.progressThresholds || "75").split(",").map(function (t) {
      return parseFloat(t.trim()) * 100;
    });
    var rewards = (cfg.progressRewards || "Free shipping").split(",").map(function (r) {
      return r.trim();
    });
    var maxThreshold = thresholdDollars[thresholdDollars.length - 1];

    var nextThreshold = null;
    var nextReward = null;
    for (var i = 0; i < thresholdDollars.length; i++) {
      if (cartTotalCents < thresholdDollars[i]) {
        nextThreshold = thresholdDollars[i];
        nextReward = rewards[i] || rewards[rewards.length - 1];
        break;
      }
    }

    var pct = Math.min((cartTotalCents / maxThreshold) * 100, 100);
    $fill.css("width", pct + "%");

    if (nextThreshold === null) {
      $msg.addClass("complete").html(
        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" style="vertical-align:middle;margin-right:4px;">' +
          '<path d="M9 12L11 14L15 10M21 12C21 16.97 16.97 21 12 21C7.03 21 3 16.97 3 12C3 7.03 7.03 3 12 3C16.97 3 21 7.03 21 12Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
          "</svg>" +
          htmlEncode(cfg.progressCompleteMessage)
      );
    } else {
      var needed = nextThreshold - cartTotalCents;
      $msg.removeClass("complete").text(
        "Spend " + formatMoney(needed) + " more to get " + nextReward + "!"
      );
    }
  }

  /* ---------------------------------------------------------------
     Build line-item HTML
  --------------------------------------------------------------- */

  function getProductVariant(product, variantId) {
    if (!product || !product.variants) return null;

    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].id === variantId) return product.variants[i];
    }

    return null;
  }

  function getAvailableVariants(product) {
    var available = [];

    if (!product || !product.variants) return available;

    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].available) {
        available.push(product.variants[i]);
      }
    }

    return available;
  }

  function getFirstAvailableVariant(product) {
    var availableVariants = getAvailableVariants(product);
    return availableVariants.length ? availableVariants[0] : null;
  }

  function itemPropertiesToArray(item) {
    var properties = item && item.properties;
    var pairs = [];

    if (!properties) return pairs;

    if (Array.isArray(properties)) {
      for (var i = 0; i < properties.length; i++) {
        if (properties[i] && properties[i].length >= 2) {
          pairs.push({ key: properties[i][0], value: properties[i][1] });
        }
      }
      return pairs;
    }

    Object.keys(properties).forEach(function (key) {
      pairs.push({ key: key, value: properties[key] });
    });

    return pairs;
  }

  function hasLineLevelDiscountAllocations(item) {
    return !!(
      item &&
      item.line_level_discount_allocations &&
      item.line_level_discount_allocations.length
    );
  }

  function hasLegacyDiscountEntries(item) {
    return !!(
      item &&
      item.discounts &&
      item.discounts.length
    );
  }

  function getItemDiscountDetails(item) {
    var details = [];

    if (hasLineLevelDiscountAllocations(item)) {
      for (var i = 0; i < item.line_level_discount_allocations.length; i++) {
        var allocation = item.line_level_discount_allocations[i];
        if (
          allocation &&
          allocation.discount_application &&
          allocation.discount_application.title
        ) {
          details.push({
            title: allocation.discount_application.title,
            amount: allocation.amount || 0
          });
        }
      }

      return details;
    }

    if (item && item.discounts && item.discounts.length) {
      for (var j = 0; j < item.discounts.length; j++) {
        if (item.discounts[j] && item.discounts[j].title) {
          details.push({
            title: item.discounts[j].title,
            amount: item.discounts[j].amount || 0
          });
        }
      }
    }

    return details;
  }

  function getPromoKeywordRegex() {
    var rewardTerms = ((cfg.progressRewards || "") + ",free gift,free item,bonus item,promo,promotion,offer,upsell,gift")
      .split(",")
      .map(function (term) {
        return String(term || "").trim().toLowerCase();
      })
      .filter(function (term) {
        return term.length > 0;
      });

    if (!rewardTerms.length) {
      return /\b(gift|bonus|reward|promo|promotion|offer|upsell|free)\b/i;
    }

    var escaped = rewardTerms.map(function (term) {
      return term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    });

    return new RegExp(escaped.join("|"), "i");
  }

  function isManagedPromoItem(item) {
    if (!item) return false;

    var propertyPairs = itemPropertiesToArray(item);
    var promoKeywordRegex = getPromoKeywordRegex();
    var hasOfferProperty = propertyPairs.some(function (pair) {
      var key = String(pair.key || "");
      var value = String(pair.value || "");

      if (!value) return false;

      return (
        /^_(ocu_offer_id|saso.*|.*gift.*|.*promo.*|.*offer.*|.*upsell.*)$/i.test(key) ||
        (/^_/.test(key) && promoKeywordRegex.test(value))
      );
    });

    if (hasOfferProperty) return true;

    var isFreeViaDiscount = item.final_price === 0 && item.original_price > 0;
    if (!isFreeViaDiscount) return false;

    if (hasLineLevelDiscountAllocations(item) || hasLegacyDiscountEntries(item)) return true;

    return promoKeywordRegex.test(String(item.title || "")) || promoKeywordRegex.test(String(item.product_title || ""));
  }

  function isManagedPromoElement($item) {
    return $item && $item.attr("data-slidecart-managed-promo") === "true";
  }

  function isItemMutationPending($item) {
    return $item && ($item.hasClass("removing") || $item.hasClass("cart-mutation-pending"));
  }

  function buildItemPriceHtml(item, product) {
    if (isManagedPromoItem(item) && item.final_price === 0) {
      return htmlEncode(cfg.translations.freePriceText || "Free");
    }

    var variant = getProductVariant(product, item.id);
    var compareAtPrice = variant && variant.compare_at_price ? variant.compare_at_price : 0;
    var hasCartDiscount = item.original_price > item.final_price;

    if (hasCartDiscount) {
      return (
        '<span class="sale-price">' + formatMoney(item.final_price) + "</span>" +
        '<span class="was-price">' + formatMoney(item.original_price) + "</span>"
      );
    }

    if (compareAtPrice > item.final_price) {
      return (
        '<span class="sale-price">' + formatMoney(item.final_price) + "</span>" +
        '<span class="was-price">' + formatMoney(compareAtPrice) + "</span>"
      );
    }

    return formatMoney(item.final_price);
  }

  function updateDrawerTitle(itemCount) {
    var $title = $("[data-slidecart-title]");
    if (!$title.length) return;

    var titleText = cfg.titleText || "Cart";
    $title.contents().filter(function () {
      return this.nodeType === 3;
    }).remove();
    $title.prepend(document.createTextNode(titleText + " "));

    var $count = $("[data-slidecart-title-count]");
    if (!$count.length) return;

    if (cfg.showTitleItemCount) {
      $count.text("(" + itemCount + ")").show();
    } else {
      $count.hide();
    }
  }

  function getDrawerFocusableElements() {
    return $("#slidecart-wrapper")
      .find('a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')
      .filter(":visible");
  }

  function focusDrawer() {
    var $wrapper = $("#slidecart-wrapper");
    var $focusable = getDrawerFocusableElements();

    if ($focusable.length) {
      $focusable.first().trigger("focus");
      return;
    }

    $wrapper.trigger("focus");
  }

  function buildItemDiscountHtml(item) {
    var html = "";
    var discountDetails = getItemDiscountDetails(item);

    for (var d = 0; d < discountDetails.length; d++) {
      html +=
        '<div class="slidecart-item-discount">' +
        htmlEncode(discountDetails[d].title) +
        " (-" + formatMoney(discountDetails[d].amount) + ")" +
        "</div>";
    }

    return html;
  }

  function getItemRenderSignature(item, product) {
    var variant = getProductVariant(product, item.id);
    var planData = product ? computePlanData(item, product) : null;

    return JSON.stringify({
      item: item,
      compareAtPrice: variant ? variant.compare_at_price : null,
      sellingPlans: planData ? planData.sellingPlans : null,
    });
  }

  function buildItemHtml(item, index, planData, product) {
    var lineId = index + 1;
    var isManagedPromo = isManagedPromoItem(item);
    var imageHtml = item.image
      ? '<img src="' + imgResize(item.image, "compact") + '" alt="' + htmlEncode(item.title) + '" />'
      : "";
    var priceHtml = buildItemPriceHtml(item, product);

    var discountHtml = buildItemDiscountHtml(item);

    var variantHtml = "";
    if (item.variant_title && item.variant_title !== "Default Title") {
      variantHtml = '<div class="slidecart-item-variant">' + htmlEncode(item.variant_title) + "</div>";
    }

    var sellingPlanHtml = isManagedPromo ? "" : buildSellingPlanHtml(item, planData);
    var actionsHtml = "";

    if (!isManagedPromo) {
      actionsHtml =
        '<div class="slidecart-item-actions">' +
          '<div class="slidecart-qty-wrapper">' +
            '<button type="button" data-slidecart-qty="minus" data-key="' + item.key + '" aria-label="Decrease quantity">&#8722;</button>' +
            '<input type="number" min="1" value="' + item.quantity + '" data-slidecart-qty-input data-key="' + item.key + '" aria-label="Quantity" />' +
            '<button type="button" data-slidecart-qty="plus" data-key="' + item.key + '" aria-label="Increase quantity">&#43;</button>' +
          "</div>" +
          '<button type="button" class="slidecart-remove" data-slidecart-remove data-key="' + item.key + '">' +
            (cfg.translations ? cfg.translations.remove : "Remove") +
          "</button>" +
        "</div>";
    }

    return (
      '<div class="slidecart-item' + (isManagedPromo ? " slidecart-item--managed-promo" : "") + '" data-slidecart-item data-line="' + lineId + '" data-key="' + item.key + '" data-variant-id="' + item.id + '" data-slidecart-managed-promo="' + isManagedPromo + '">' +
        '<div class="slidecart-item-image"><a href="' + item.url + '">' + imageHtml + "</a></div>" +
        '<div class="slidecart-item-details">' +
          '<div class="slidecart-item-title"><a href="' + item.url + '">' + htmlEncode(item.product_title) + "</a></div>" +
          variantHtml +
          '<div class="slidecart-item-price">' + priceHtml + "</div>" +
          discountHtml +
          sellingPlanHtml +
          actionsHtml +
          '<div class="slidecart-item-loading" data-slidecart-item-loading>Updating…</div>' +
        "</div>" +
      "</div>"
    );
  }

  function syncCartItems($items, cart) {
    var existingItems = {};
    var existingItemsByIdentity = {};
    var renderedItems = [];
    var retainedElements = [];

    $items.find("[data-slidecart-item]").each(function () {
      var $item = $(this);
      existingItems[$item.attr("data-key")] = $item;

      var identity = $item.data("slidecartIdentity");
      if (identity) existingItemsByIdentity[identity] = $item;
    });

    $.each(cart.items, function (index, item) {
      var product = productCache[item.handle];
      var planData = product ? computePlanData(item, product) : null;
      var signature = getItemRenderSignature(item, product);
      var identity = getCartItemIdentity(item);
      var $existing = existingItems[item.key] || existingItemsByIdentity[identity];
      var $rendered;

      if ($existing && $existing.data("slidecartSignature") === signature) {
        $rendered = $existing;
        $rendered.attr({
          "data-line": index + 1,
          "data-key": item.key,
        });
        $rendered.find("[data-key]").attr("data-key", item.key);
        $rendered.data("slidecartIdentity", identity);
      } else {
        $rendered = $(buildItemHtml(item, index, planData, product));
        $rendered.data("slidecartSignature", signature);
        $rendered.data("slidecartIdentity", identity);

        if ($existing) {
          $existing.replaceWith($rendered);
        } else {
          $rendered.addClass("slidecart-item--new");
          (function ($newItem) {
            setTimeout(function () {
              $newItem.removeClass("slidecart-item--new");
            }, 300);
          })($rendered);
        }
      }

      retainedElements.push($rendered[0]);
      renderedItems.push($rendered);
    });

    $items.find("[data-slidecart-item]").each(function () {
      if (retainedElements.indexOf(this) === -1) $(this).remove();
    });

    // Move only out-of-order nodes; unchanged rows retain focus and state.
    var $previous = null;
    for (var i = 0; i < renderedItems.length; i++) {
      var $current = renderedItems[i];

      if (!$previous) {
        var $first = $items.children("[data-slidecart-item]").first();
        if (!$first.length || $first[0] !== $current[0]) {
          $items.prepend($current);
        }
      } else if (!$previous.next().is($current)) {
        $previous.after($current);
      }

      $previous = $current;
    }
  }

  /* ---------------------------------------------------------------
     Selling plan support
  --------------------------------------------------------------- */

  function buildSellingPlanHtml(item, planData) {
    if (!planData || !planData.sellingPlans) return "";

    var html = '<div class="slidecart-item-selling-plan">';
    var plans = planData.sellingPlans;
    var currentPlanId = item.selling_plan_allocation
      ? item.selling_plan_allocation.selling_plan.id
      : null;

    html += '<select data-slidecart-selling-plan data-key="' + item.key + '" data-variant-id="' + item.id + '" data-qty="' + item.quantity + '">';
    html += '<option value=""' + (!currentPlanId ? " selected" : "") + '>One-Time Purchase</option>';

    for (var i = 0; i < plans.length; i++) {
      var selected = plans[i].id === currentPlanId ? " selected" : "";
      html += '<option value="' + plans[i].id + '"' + selected + ">" + htmlEncode(plans[i].name) + "</option>";
    }

    html += "</select>";

    if (!currentPlanId && plans.length > 0) {
      html +=
        '<button type="button" class="slidecart-subscribe-toggle" data-slidecart-subscribe ' +
        'data-key="' + item.key + '" data-variant-id="' + item.id + '" ' +
        'data-qty="' + item.quantity + '" data-plan-id="' + plans[0].id + '">' +
        "Subscribe &amp; Save</button>";
    }

    html += "</div>";
    return html;
  }

  function computePlanData(item, product) {
    if (!product.selling_plan_groups || product.selling_plan_groups.length === 0) return null;

    var spg = product.selling_plan_groups;
    var plans = spg.length > 0 ? spg[0].selling_plans : null;

    return { sellingPlans: plans };
  }

  /* ---------------------------------------------------------------
     Static upsell product
  --------------------------------------------------------------- */

  function buildUpsellPriceHtml(variant) {
    if (!variant) return "";
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      return '<span class="sale-price">' + formatMoney(variant.price) + '</span>' +
             '<span class="was-price">' + formatMoney(variant.compare_at_price) + '</span>';
    }
    return formatMoney(variant.price);
  }

  function renderStaticUpsell(cart, renderToken) {
    if (!cfg.upsellEnabled || !cfg.upsellProductHandle) return;

    var $upsell = $("[data-slidecart-upsell]");
    var $card = $("[data-slidecart-upsell-card]");
    if (!$upsell.length) return;

    // Hide if cart is empty
    if (cart.item_count === 0) {
      $upsell.hide();
      return;
    }

    var handle = cfg.upsellProductHandle;

    function render(product) {
      if (renderToken !== renderVersion) return;

      // Check if any variant of this product is already in the cart
      var cartVariantIds = {};
      for (var i = 0; i < cart.items.length; i++) {
        cartVariantIds[cart.items[i].id] = true;
      }

      var isInCart = false;
      for (var v = 0; v < product.variants.length; v++) {
        if (cartVariantIds[product.variants[v].id]) {
          isInCart = true;
          break;
        }
      }

      if (isInCart) {
        $upsell.hide();
        return;
      }

      var availableVariants = getAvailableVariants(product);
      var firstVariant = availableVariants[0];

      if (!firstVariant) {
        $upsell.hide();
        return;
      }

      var imageUrl = product.featured_image ? imgResize(product.featured_image, "compact") : "";
      var imageHtml = imageUrl
        ? '<a href="' + product.url + '"><img src="' + imageUrl + '" alt="' + htmlEncode(product.title) + '" /></a>'
        : "";

      var variantHtml = "";
      if (availableVariants.length > 1) {
        variantHtml = '<select class="slidecart-upsell-variant-select" id="slidecart-upsell-variant-' + firstVariant.id + '" name="slidecart_upsell_variant" data-slidecart-upsell-variant aria-label="' + htmlEncode("Choose variant for " + product.title) + '">';
        for (var vi = 0; vi < availableVariants.length; vi++) {
          var pv = availableVariants[vi];
          variantHtml += '<option value="' + pv.id + '" data-price="' + pv.price + '"' +
                         ' data-compare-price="' + (pv.compare_at_price || 0) + '">' +
                         htmlEncode(pv.title) + '</option>';
        }
        variantHtml += '</select>';
      }

      var html =
        '<div class="slidecart-upsell-image">' + imageHtml + '</div>' +
        '<div class="slidecart-upsell-details">' +
          '<div class="slidecart-upsell-title"><a href="' + product.url + '">' + htmlEncode(product.title) + '</a></div>' +
          '<div class="slidecart-upsell-price" data-slidecart-upsell-price>' + buildUpsellPriceHtml(firstVariant) + '</div>' +
          '<div class="slidecart-upsell-actions">' + variantHtml + '</div>' +
        '</div>' +
        '<div class="slidecart-upsell-cta">' +
          '<button type="button" class="slidecart-upsell-add" data-slidecart-upsell-add data-variant-id="' + firstVariant.id + '">Add</button>' +
        '</div>';

      if ($card.data("slidecartHtml") !== html) {
        $card.html(html).data("slidecartHtml", html);
      }
      $upsell.show();
    }

    if (productCache[handle]) {
      render(productCache[handle]);
    } else {
      $.getJSON(shopifyRoot + "products/" + handle + ".js")
        .done(function (product) {
          productCache[handle] = product;
          render(product);
        })
        .fail(function () {
          if (renderToken !== renderVersion) return;
          $upsell.hide();
        });
    }
  }

  /* ---------------------------------------------------------------
     Recommended products
  --------------------------------------------------------------- */

  var recsCache = {};

  function getProductUrl(product) {
    if (product && product.url) return product.url;
    if (product && product.handle) return shopifyRoot.replace(/\/?$/, "/") + "products/" + product.handle;
    return "#";
  }

  function buildRecCardHtml(product) {
    var availableVariants = getAvailableVariants(product);
    var firstAvailable = availableVariants[0];

    if (!firstAvailable) return "";

    var imageUrl = product.featured_image ? imgResize(product.featured_image, "compact") : "";
    var imageHtml = imageUrl ? '<img src="' + imageUrl + '" alt="' + htmlEncode(product.title) + '" />' : "";

    var variantHtml = "";
    if (availableVariants.length > 1) {
      variantHtml = '<select class="slidecart-rec-variant-select" id="slidecart-rec-variant-' + product.id + '" name="slidecart_recommendation_variant" data-slidecart-rec-variant data-product-handle="' + product.handle + '" aria-label="' + htmlEncode("Choose variant for " + product.title) + '">';
      for (var vi = 0; vi < availableVariants.length; vi++) {
        var pv = availableVariants[vi];
        var selected = pv.id === firstAvailable.id ? " selected" : "";
        variantHtml += '<option value="' + pv.id + '" data-price="' + pv.price + '"' +
                       ' data-compare-price="' + (pv.compare_at_price || 0) + '"' + selected + '>' +
                       htmlEncode(pv.title) + '</option>';
      }
      variantHtml += '</select>';
    }

    var priceHtml = buildUpsellPriceHtml(firstAvailable);
    var productUrl = getProductUrl(product);

    return (
      '<div class="slidecart-rec-card" data-slidecart-rec-card data-product-id="' + product.id + '">' +
        '<div class="slidecart-rec-image"><a href="' + productUrl + '">' + imageHtml + '</a></div>' +
        '<div class="slidecart-rec-details">' +
          '<div class="slidecart-rec-title"><a href="' + productUrl + '">' + htmlEncode(product.title) + '</a></div>' +
          '<div class="slidecart-rec-price" data-slidecart-rec-price>' + priceHtml + '</div>' +
          '<div class="slidecart-rec-actions">' + variantHtml + '</div>' +
        '</div>' +
        '<div class="slidecart-rec-cta">' +
          '<button type="button" class="slidecart-rec-add" data-slidecart-rec-add data-variant-id="' + firstAvailable.id + '">Add</button>' +
        '</div>' +
      '</div>'
    );
  }

  function renderRecommendations(cart, renderToken) {
    if (!cfg.recommendationsEnabled) return;

    var $section = $("[data-slidecart-recommendations]");
    var $list = $("[data-slidecart-recommendations-list]");
    if (!$section.length) return;

    // Hide if cart is empty
    if (cart.item_count === 0) {
      $section.hide();
      return;
    }

    // Use first cart item's product_id as the seed
    var seedProductId = cart.items[0].product_id;
    var limit = cfg.recommendationsLimit || 4;
    var overrideHandles = cfg.recommendationOverrideHandles || [];

    // Build set of product IDs already in cart
    var cartProductIds = {};
    for (var i = 0; i < cart.items.length; i++) {
      cartProductIds[cart.items[i].product_id] = true;
    }

    function renderProducts(products) {
      if (renderToken !== renderVersion) return;

      // Filter out products already in cart
      var filtered = [];
      for (var p = 0; p < products.length; p++) {
        if (!cartProductIds[products[p].id] && getFirstAvailableVariant(products[p])) {
          filtered.push(products[p]);
        }
      }

      if (filtered.length === 0) {
        $section.hide();
        return;
      }

      // Cap to configured limit
      if (filtered.length > limit) {
        filtered = filtered.slice(0, limit);
      }

      var html = "";
      for (var f = 0; f < filtered.length; f++) {
        html += buildRecCardHtml(filtered[f]);
      }

      if ($list.data("slidecartHtml") !== html) {
        $list.html(html).data("slidecartHtml", html);
      }
      $section.show();
    }

    if (overrideHandles.length) {
      var normalizedHandles = overrideHandles.filter(function (handle) {
        return !!handle;
      });

      if (!normalizedHandles.length) {
        $section.hide();
        return;
      }

      var curatedProducts = new Array(normalizedHandles.length);
      var pending = 0;

      function finishCuratedRender() {
        if (pending !== 0) return;
        renderProducts(curatedProducts.filter(function (product) {
          return !!product;
        }));
      }

      for (var h = 0; h < normalizedHandles.length; h++) {
        (function (handle, index) {
          if (productCache[handle]) {
            curatedProducts[index] = productCache[handle];
            return;
          }

          pending++;
          $.getJSON(shopifyRoot + "products/" + handle + ".js")
            .done(function (product) {
              productCache[handle] = product;
              curatedProducts[index] = product;
            })
            .fail(function () {
              curatedProducts[index] = null;
            })
            .always(function () {
              pending--;
              finishCuratedRender();
            });
        })(normalizedHandles[h], h);
      }

      finishCuratedRender();
      return;
    }

    if (recsCache[seedProductId]) {
      renderProducts(recsCache[seedProductId]);
      return;
    }

    $.getJSON(shopifyRoot + "recommendations/products.json", {
      product_id: seedProductId,
      limit: limit + 4 // fetch extra to account for filtering
    })
      .done(function (data) {
        var products = data.products || [];
        recsCache[seedProductId] = products;
        renderProducts(products);
      })
      .fail(function () {
        if (renderToken !== renderVersion) return;
        $section.hide();
      });
  }

  /* ---------------------------------------------------------------
     Render cart
  --------------------------------------------------------------- */

  function renderCart() {
    if (cartMutationRunning || cartMutationQueue.length) {
      cartRenderPending = true;
      return;
    }

    cartRenderPending = false;
    renderVersion++;
    var currentVersion = renderVersion;

    $.getJSON(shopifyRoot + "cart.js", function (cart) {
      if (currentVersion !== renderVersion) return;
      latestCart = cart;

      var $items = $("[data-slidecart-items]");
      var $empty = $("[data-slidecart-empty]");
      var $footer = $("[data-slidecart-footer]");
      var $subtotalPrice = $("[data-slidecart-subtotal-price]");

      $(".cart_count").each(function () {
        var el = $(this);
        var val = el.text() || "0";
        el.text(val.replace(/[0-9]+/, cart.item_count));
      });

      updateDrawerTitle(cart.item_count);

      if (cart.item_count === 0) {
        $items.find(".slidecart-item").remove();
        $("[data-slidecart-recommendations]").hide();
        $("[data-slidecart-upsell]").hide();
        $empty.show();
        $footer.addClass("hidden");
        updateProgressBar(0);
        return;
      }

      $empty.hide();
      $footer.removeClass("hidden");
      $subtotalPrice.html('<span class="money">' + formatMoney(cart.total_price) + "</span>");
      updateProgressBar(cart.items_subtotal_price);

      var handlesToFetch = {};
      var metadataRerenderScheduled = false;

      function scheduleMetadataRerender() {
        if (metadataRerenderScheduled) return;
        metadataRerenderScheduled = true;

        setTimeout(function () {
          if (currentVersion !== renderVersion) return;
          renderCart();
        }, 0);
      }

      $.each(cart.items, function (index, item) {
        var handle = item.handle;
        var cached = productCache[handle];
        if (!cached && handle) {
          if (!handlesToFetch[handle]) handlesToFetch[handle] = [];
          handlesToFetch[handle].push({ item: item, index: index });
        }
      });

      syncCartItems($items, cart);

      Object.keys(handlesToFetch).forEach(function (handle) {
        $.getJSON(shopifyRoot + "products/" + handle + ".js")
          .done(function (product) {
            productCache[handle] = product;
            if (currentVersion !== renderVersion) return;
            scheduleMetadataRerender();
          });
      });

      // Render upsell sections
      renderRecommendations(cart, currentVersion);
      renderStaticUpsell(cart, currentVersion);

      if (typeof Currency !== "undefined" && Currency.show_multiple_currencies && typeof currencyConverter !== "undefined") {
        currencyConverter.convertCurrencies();
      }
    });
  }

  /* ---------------------------------------------------------------
     Open / close
  --------------------------------------------------------------- */

  function lockBodyScroll() {
    document.documentElement.classList.add("slidecart-open");
    document.body.classList.add("slidecart-open");
  }

  function unlockBodyScroll() {
    document.documentElement.classList.remove("slidecart-open");
    document.body.classList.remove("slidecart-open");
  }

  function open() {
    var $wrapper = $("#slidecart-wrapper");
    var $overlay = $("#slidecart-overlay");

    if ($wrapper.hasClass("active") || document.body.classList.contains("slidecart-open")) return;

    lastFocusedElement = document.activeElement;
    lockBodyScroll();
    renderCart();

    if ($wrapper.length) {
      $wrapper[0].offsetHeight;
    }

    var scheduleOpen = window.requestAnimationFrame || function (callback) {
      return setTimeout(callback, 16);
    };

    scheduleOpen(function () {
      $overlay.addClass("active");
      $wrapper.addClass("active");
      focusDrawer();
    });
  }

  function close() {
    $("#slidecart-wrapper").removeClass("active");
    $("#slidecart-overlay").removeClass("active");
    unlockBodyScroll();

    if (lastFocusedElement && document.contains(lastFocusedElement)) {
      $(lastFocusedElement).trigger("focus");
    }
  }

  window.slidecartOpen = open;
  window.slidecartClose = close;
  window.slidecartRender = renderCart;

  /* ---------------------------------------------------------------
     Cart totals update (no full rebuild)
  --------------------------------------------------------------- */

  function updateCartTotals(cart) {
    $(".cart_count").each(function () {
      var el = $(this);
      var val = el.text() || "0";
      el.text(val.replace(/[0-9]+/, cart.item_count));
    });

    updateDrawerTitle(cart.item_count);

    if (cart.item_count === 0) {
      $("[data-slidecart-items]").find(".slidecart-item").remove();
      $("[data-slidecart-recommendations]").hide();
      $("[data-slidecart-upsell]").hide();
      $("[data-slidecart-empty]").show();
      $("[data-slidecart-footer]").addClass("hidden");
      updateProgressBar(0);
    } else {
      $("[data-slidecart-empty]").hide();
      $("[data-slidecart-footer]").removeClass("hidden");
      $("[data-slidecart-subtotal-price]").html('<span class="money">' + formatMoney(cart.total_price) + "</span>");
      updateProgressBar(cart.items_subtotal_price);
    }

    if (typeof Currency !== "undefined" && Currency.show_multiple_currencies && typeof currencyConverter !== "undefined") {
      currencyConverter.convertCurrencies();
    }
  }

  /* ---------------------------------------------------------------
     Quantity change
  --------------------------------------------------------------- */

  function findQueuedQuantityMutation(key) {
    for (var i = 0; i < cartMutationQueue.length; i++) {
      if (cartMutationQueue[i].type === "quantity" && cartMutationQueue[i].key === key) {
        return cartMutationQueue[i];
      }
    }

    return null;
  }

  function updateLineFromCart(originalKey, identity, cart) {
    var $item = findSlidecartItem(originalKey, identity);
    var updatedItem = findCartItem(cart, originalKey, identity);

    if (!updatedItem) {
      $item.remove();
      updateCartTotals(cart);
      return;
    }

    $item.attr("data-key", updatedItem.key);
    $item.find("[data-key]").attr("data-key", updatedItem.key);
    $item.find("[data-slidecart-qty-input]").val(updatedItem.quantity);
    $item.find("[data-slidecart-selling-plan]").attr("data-qty", updatedItem.quantity);
    $item.find("[data-slidecart-subscribe]").attr("data-qty", updatedItem.quantity);
    $item.find(".slidecart-item-price").html(buildItemPriceHtml(updatedItem, productCache[updatedItem.handle]));
    $item.find(".slidecart-item-discount").remove();
    $item.find(".slidecart-item-price").after(buildItemDiscountHtml(updatedItem));
    $item.data("slidecartSignature", getItemRenderSignature(updatedItem, productCache[updatedItem.handle]));
    $item.data("slidecartIdentity", getCartItemIdentity(updatedItem));
    $item.find("[data-slidecart-item-loading]").removeClass("visible");
    $item.removeClass("removing");
    updateCartTotals(cart);
  }

  function changeQuantity(key, newQty) {
    var $item = $('[data-slidecart-item][data-key="' + key + '"]');
    if (isManagedPromoElement($item)) return;
    $item.find("[data-slidecart-item-loading]").addClass("visible");

    var queuedMutation = findQueuedQuantityMutation(key);
    if (queuedMutation) {
      queuedMutation.quantity = newQty;
      return;
    }

    var mutation = {
      type: "quantity",
      key: key,
      originalKey: key,
      identity: $item.data("slidecartIdentity"),
      quantity: newQty,
      execute: function () {
        var deferred = $.Deferred();

        function submit(cart) {
          var currentItem = findCartItem(cart, mutation.key, mutation.identity);

          if (!currentItem) {
            if (mutation.quantity === 0) {
              deferred.resolve(cart);
            } else {
              deferred.reject();
            }
            return;
          }

          mutation.key = currentItem.key;
          ownedAjax({
            url: shopifyRoot + "cart/change.js",
            type: "POST",
            dataType: "json",
            data: { id: mutation.key, quantity: mutation.quantity },
          }).done(function (cart) {
            deferred.resolve(cart);
          }).fail(function () {
            deferred.reject.apply(deferred, arguments);
          });
        }

        if (latestCart) {
          submit(latestCart);
        } else {
          $.getJSON(shopifyRoot + "cart.js")
            .done(function (cart) {
              latestCart = cart;
              submit(cart);
            })
            .fail(function () {
              deferred.reject.apply(deferred, arguments);
            });
        }

        return deferred.promise();
      },
      onSuccess: function (cart) {
        updateLineFromCart(mutation.originalKey, mutation.identity, cart);
      },
      onError: function () {
        var $currentItem = findSlidecartItem(mutation.originalKey, mutation.identity);
        $currentItem.removeClass("removing");
        $currentItem.find("[data-slidecart-item-loading]").removeClass("visible");
        cartRenderPending = true;
      },
    };

    enqueueCartMutation(mutation);
  }

  /* ---------------------------------------------------------------
     Selling plan change
  --------------------------------------------------------------- */

  function changeSellingPlan($select) {
    var key = $select.attr("data-key");
    var $item = $('[data-slidecart-item][data-key="' + key + '"]');
    if (isManagedPromoElement($item)) return;
    var variantId = parseInt($select.attr("data-variant-id"), 10);
    var qty = parseInt($item.find("[data-slidecart-qty-input]").val(), 10) || 1;
    var newPlan = $select.val();
    var newPlanInt = newPlan ? parseInt(newPlan, 10) : null;
    var identity = $item.data("slidecartIdentity");

    $item.addClass("cart-mutation-pending");
    $item.find("[data-slidecart-item-loading]").addClass("visible");

    var mutation = {
      type: "selling-plan",
      key: key,
      execute: function () {
        var deferred = $.Deferred();

        resolveCurrentCartItem(mutation.key, identity)
          .done(function (currentItem) {
            mutation.key = currentItem.key;
            var currentQty = currentItem.quantity || qty;
            var currentVariantId = currentItem.id || variantId;
            var properties = currentItem.properties || {};
            var currentPlanId = currentItem.selling_plan_allocation
              ? currentItem.selling_plan_allocation.selling_plan.id
              : null;

            ownedAjax({
              url: shopifyRoot + "cart/change.js",
              type: "POST",
              dataType: "json",
              data: { id: mutation.key, quantity: 0 },
            }).done(function () {
              var addData = { items: [{ quantity: currentQty, id: currentVariantId, properties: properties }] };
              if (newPlanInt) addData.items[0].selling_plan = newPlanInt;

              ownedAjax({
                url: shopifyRoot + "cart/add.js",
                type: "POST",
                dataType: "json",
                contentType: "application/json",
                data: JSON.stringify(addData),
              }).done(function (result) {
                deferred.resolve(result);
              }).fail(function () {
                // Restore original item before reporting failure.
                var restoreData = { items: [{ quantity: currentQty, id: currentVariantId, properties: properties }] };
                if (currentPlanId) restoreData.items[0].selling_plan = currentPlanId;

                ownedAjax({
                  url: shopifyRoot + "cart/add.js",
                  type: "POST",
                  dataType: "json",
                  contentType: "application/json",
                  data: JSON.stringify(restoreData),
                }).always(function () {
                  deferred.reject();
                });
              });
            }).fail(function () {
              deferred.reject();
            });
          })
          .fail(function () {
            deferred.reject();
          });

        return deferred.promise();
      },
      onError: function () {
        $item.removeClass("cart-mutation-pending");
        $item.find("[data-slidecart-item-loading]").removeClass("visible");
        cartRenderPending = true;
      },
    };

    enqueueCartMutation(mutation);
  }

  /* ---------------------------------------------------------------
     Event bindings
  --------------------------------------------------------------- */

  $(document).ready(function () {
    installExternalCartRefreshBridge();

    // Close
    $(document).on("click", "[data-close-slidecart], [data-slidecart-overlay]", function () {
      close();
    });

    // ESC key
    $(document).on("keydown", function (e) {
      if (e.key === "Escape" && $("#slidecart-wrapper").hasClass("active")) {
        close();
        return;
      }

      if (e.key !== "Tab" || !$("#slidecart-wrapper").hasClass("active")) {
        return;
      }

      var $focusable = getDrawerFocusableElements();
      if (!$focusable.length) {
        e.preventDefault();
        $("#slidecart-wrapper").trigger("focus");
        return;
      }

      var first = $focusable.get(0);
      var last = $focusable.get($focusable.length - 1);

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        $(last).trigger("focus");
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        $(first).trigger("focus");
      }
    });

    // Cart icon links — intercept and open slidecart instead
    $(document).on("click", "[data-slidecart-trigger]", function (e) {
      e.preventDefault();
      open();
    });

    // Quantity buttons (debounced to prevent overlapping AJAX on rapid clicks)
    var qtyBtnTimers = {};
    $(document).on("click", "[data-slidecart-qty]", function (e) {
      e.preventDefault();
      var $item = $(this).closest("[data-slidecart-item]");
      if (isManagedPromoElement($item) || isItemMutationPending($item)) return;
      var key = $(this).attr("data-key");
      var $input = $(this).siblings("[data-slidecart-qty-input]");
      var current = parseInt($input.val(), 10) || 1;
      var newQty;

      if ($(this).attr("data-slidecart-qty") === "plus") {
        newQty = current + 1;
      } else {
        newQty = Math.max(current - 1, 0);
      }

      $input.val(newQty);
      clearTimeout(qtyBtnTimers[key]);
      qtyBtnTimers[key] = setTimeout(function () {
        changeQuantity(key, newQty);
      }, 350);
    });

    // Quantity input direct edit
    var qtyInputTimers = {};
    $(document).on("change", "[data-slidecart-qty-input]", function () {
      var $item = $(this).closest("[data-slidecart-item]");
      if (isManagedPromoElement($item) || isItemMutationPending($item)) return;
      var key = $(this).attr("data-key");
      var newQty = Math.max(parseInt($(this).val(), 10) || 0, 0);
      clearTimeout(qtyInputTimers[key]);
      qtyInputTimers[key] = setTimeout(function () {
        changeQuantity(key, newQty);
      }, 400);
    });

    // Remove
    $(document).on("click", "[data-slidecart-remove]", function (e) {
      e.preventDefault();
      var key = $(this).attr("data-key");
      var $item = $(this).closest("[data-slidecart-item]");
      if (isManagedPromoElement($item) || isItemMutationPending($item)) return;
      clearTimeout(qtyBtnTimers[key]);
      clearTimeout(qtyInputTimers[key]);
      $item.addClass("removing");
      changeQuantity(key, 0);
    });

    // Selling plan dropdown change
    $(document).on("change", "[data-slidecart-selling-plan]", function () {
      var $item = $(this).closest("[data-slidecart-item]");
      if (isManagedPromoElement($item) || $item.hasClass("cart-mutation-pending")) return;
      changeSellingPlan($(this));
    });

    // Subscribe & Save toggle
    $(document).on("click", "[data-slidecart-subscribe]", function (e) {
      e.preventDefault();
      var $btn = $(this);
      var key = $btn.attr("data-key");
      var variantId = parseInt($btn.attr("data-variant-id"), 10);
      var $item = $btn.closest("[data-slidecart-item]");
      var qty = parseInt($item.find("[data-slidecart-qty-input]").val(), 10) || 1;
      var planId = parseInt($btn.attr("data-plan-id"), 10);
      var identity = $item.data("slidecartIdentity");

      if (isManagedPromoElement($item) || $item.hasClass("cart-mutation-pending")) return;
      $item.addClass("cart-mutation-pending");
      $item.find("[data-slidecart-item-loading]").addClass("visible");

      var mutation = {
        type: "subscribe",
        key: key,
        execute: function () {
          var deferred = $.Deferred();

          resolveCurrentCartItem(mutation.key, identity)
            .done(function (currentItem) {
              mutation.key = currentItem.key;
              var currentQty = currentItem.quantity || qty;
              var currentVariantId = currentItem.id || variantId;
              var properties = currentItem.properties || {};

              ownedAjax({
                url: shopifyRoot + "cart/change.js",
                type: "POST",
                dataType: "json",
                data: { id: mutation.key, quantity: 0 },
              }).done(function () {
                ownedAjax({
                  url: shopifyRoot + "cart/add.js",
                  type: "POST",
                  dataType: "json",
                  contentType: "application/json",
                  data: JSON.stringify({
                    items: [{
                      quantity: currentQty,
                      id: currentVariantId,
                      properties: properties,
                      selling_plan: planId,
                    }],
                  }),
                }).done(function (result) {
                  deferred.resolve(result);
                }).fail(function () {
                  // Restore original one-time item before reporting failure.
                  ownedAjax({
                    url: shopifyRoot + "cart/add.js",
                    type: "POST",
                    dataType: "json",
                    contentType: "application/json",
                    data: JSON.stringify({
                      items: [{ quantity: currentQty, id: currentVariantId, properties: properties }],
                    }),
                  }).always(function () {
                    deferred.reject();
                  });
                });
              }).fail(function () {
                deferred.reject();
              });
            })
            .fail(function () {
              deferred.reject();
            });

          return deferred.promise();
        },
        onError: function () {
          $item.removeClass("cart-mutation-pending");
          $item.find("[data-slidecart-item-loading]").removeClass("visible");
          cartRenderPending = true;
        },
      };

      enqueueCartMutation(mutation);
    });

    // Upsell variant dropdown — update price and button variant ID
    $(document).on("change", "[data-slidecart-upsell-variant]", function () {
      var $opt = $(this).find(":selected");
      var price = parseInt($opt.attr("data-price"), 10);
      var comparePrice = parseInt($opt.attr("data-compare-price"), 10) || 0;
      var variantId = $(this).val();
      var $card = $(this).closest(".slidecart-upsell-card");

      $card.find("[data-slidecart-upsell-add]").attr("data-variant-id", variantId);

      var priceHtml;
      if (comparePrice > price) {
        priceHtml = '<span class="sale-price">' + formatMoney(price) + '</span>' +
                    '<span class="was-price">' + formatMoney(comparePrice) + '</span>';
      } else {
        priceHtml = formatMoney(price);
      }
      $card.find("[data-slidecart-upsell-price]").html(priceHtml);
    });

    // Upsell add-to-cart
    $(document).on("click", "[data-slidecart-upsell-add]", function (e) {
      e.preventDefault();
      var $btn = $(this);
      var variantId = parseInt($btn.attr("data-variant-id"), 10);
      $btn.prop("disabled", true).text("Adding…");

      enqueueCartMutation({
        type: "upsell-add",
        execute: function () {
          return ownedAjax({
            url: shopifyRoot + "cart/add.js",
            type: "POST",
            dataType: "json",
            contentType: "application/json",
            data: JSON.stringify({ items: [{ quantity: 1, id: variantId }] }),
          });
        },
        onError: function () {
          $btn.prop("disabled", false).text("Add");
        },
      });
    });

    // Recommendation variant dropdown — update price and button variant ID
    $(document).on("change", "[data-slidecart-rec-variant]", function () {
      var $opt = $(this).find(":selected");
      var price = parseInt($opt.attr("data-price"), 10);
      var comparePrice = parseInt($opt.attr("data-compare-price"), 10) || 0;
      var variantId = $(this).val();
      var $card = $(this).closest(".slidecart-rec-card");

      $card.find("[data-slidecart-rec-add]").attr("data-variant-id", variantId);

      var priceHtml;
      if (comparePrice > price) {
        priceHtml = '<span class="sale-price">' + formatMoney(price) + '</span>' +
                    '<span class="was-price">' + formatMoney(comparePrice) + '</span>';
      } else {
        priceHtml = formatMoney(price);
      }
      $card.find("[data-slidecart-rec-price]").html(priceHtml);
    });

    // Recommendation add-to-cart
    $(document).on("click", "[data-slidecart-rec-add]", function (e) {
      e.preventDefault();
      var $btn = $(this);
      var variantId = parseInt($btn.attr("data-variant-id"), 10);
      $btn.prop("disabled", true).text("Adding…");

      enqueueCartMutation({
        type: "recommendation-add",
        execute: function () {
          return ownedAjax({
            url: shopifyRoot + "cart/add.js",
            type: "POST",
            dataType: "json",
            contentType: "application/json",
            data: JSON.stringify({ items: [{ quantity: 1, id: variantId }] }),
          });
        },
        onError: function () {
          $btn.prop("disabled", false).text("Add");
        },
      });
    });

    // Terms of service — block checkout if unchecked
    $(document).on("submit", ".slidecart-checkout-form", function (e) {
      if (!cfg.displayTosCheckbox) return;
      var $checkbox = $("[data-slidecart-tos]");
      if ($checkbox.length && !$checkbox.is(":checked")) {
        e.preventDefault();
        $("[data-slidecart-tos-warning]").show();
        return false;
      }
    });

    // Hide TOS warning when checkbox is checked
    $(document).on("change", "[data-slidecart-tos]", function () {
      if ($(this).is(":checked")) {
        $("[data-slidecart-tos-warning]").hide();
      }
    });

    // Order note — save via AJAX on blur
    var noteTimer;
    $(document).on("input", "[data-slidecart-note]", function () {
      var note = $(this).val();
      clearTimeout(noteTimer);
      noteTimer = setTimeout(function () {
        var queuedNote = null;
        for (var i = 0; i < cartMutationQueue.length; i++) {
          if (cartMutationQueue[i].type === "note") {
            queuedNote = cartMutationQueue[i];
            break;
          }
        }

        if (queuedNote) {
          queuedNote.note = note;
          return;
        }

        var noteMutation = {
          type: "note",
          note: note,
          reconcile: false,
          invalidateRender: false,
          execute: function () {
            return ownedAjax({
              url: shopifyRoot + "cart/update.js",
              type: "POST",
              dataType: "json",
              data: { note: noteMutation.note },
            });
          },
        };

        enqueueCartMutation(noteMutation);
      }, 500);
    });

    // AJAX add-to-cart intercept for all forms with .ajax-submit
    $(document).on("click", ".ajax-submit", function (e) {
      e.preventDefault();
      var $form = $(this).closest("form");
      var $btn = $(this);

      if ($btn.prop("disabled")) return false;

      if ($("body").hasClass("cart")) {
        $form.submit();
        return;
      }

      var formData = $form.serialize();
      $btn
        .prop("disabled", true)
        .attr("aria-busy", "true")
        .removeClass("is-added")
        .addClass("disabled is-loading");
      $btn.find("span.text").addClass("animated zoomOut");

      enqueueCartMutation({
        type: "product-add",
        execute: function () {
          return ownedAjax({
            url: shopifyRoot + "cart/add.js",
            type: "POST",
            dataType: "json",
            data: formData,
          });
        },
        onSuccess: function () {
          $btn.removeClass("is-loading").addClass("is-added");
          $btn.find(".checkmark").addClass("checkmark-active");

          setTimeout(function () {
            if ($("body").hasClass("fancybox-active")) {
              $.fancybox.close();
            }

            open();
          }, 450);

          setTimeout(function () {
            $btn
              .prop("disabled", false)
              .removeAttr("aria-busy")
              .removeClass("disabled is-added");
            $btn.find(".checkmark").removeClass("checkmark-active");
            $btn.find("span.text").removeClass("zoomOut").addClass("fadeInDown");
          }, 1000);
        },
        onError: function (xhr) {
          var response;
          try {
            response = JSON.parse(xhr.responseText);
          } catch (_e) {
            response = { description: "Could not add item to cart." };
          }
          $(".warning").remove();
          var warning = '<p class="warning animated bounceIn">' + response.description.replace("All 1 ", "All ") + "</p>";
          $form.after(warning);
          $btn
            .prop("disabled", false)
            .removeAttr("aria-busy")
            .removeClass("disabled is-loading is-added");
          var addLabel = (typeof Shopify !== "undefined" && Shopify.translation && Shopify.translation.add_to_cart) || $btn.attr("data-label") || "Add to Cart";
          $btn.find("span.text").text(addLabel).removeClass("zoomOut").addClass("zoomIn");
        },
      });

      return false;
    });
  });
})(jQuery);
